/**
 * Self-contained Hebrew calendar utilities.
 * Converts Gregorian ↔ Hebrew dates and computes Jewish holidays.
 * Algorithm: Reingold & Dershowitz "Calendrical Calculations".
 */

// ─── Core Math ────────────────────────────────────────────────────────────────

// JDN of 1 Tishri, year 1 AM — offset +1 from the astronomical epoch so that
// Hebrew dates align with the civil convention (daytime of the Jewish day, not the sunset start).
const EPOCH = 347998;

function isLeap(year: number): boolean {
  return (7 * year + 1) % 19 < 7;
}

function elapsedDays(year: number): number {
  const m = Math.floor((235 * year - 234) / 19);
  const p = 12084 + 13753 * m;
  let d = m * 29 + Math.floor(p / 25920);
  if ((3 * (d + 1)) % 7 < 3) d++;
  return d;
}

function newYear(year: number): number {
  const e = elapsedDays(year);
  const e1 = elapsedDays(year + 1);
  const e0 = elapsedDays(year - 1);
  let c = 0;
  if (e1 - e === 356) c = 2;
  else if (e - e0 === 382) c = 1;
  return EPOCH + e + c;
}

function monthLen(month: number, year: number): number {
  const yearLen = newYear(year + 1) - newYear(year);
  if ([1, 3, 5, 7, 11].includes(month)) return 30;
  if ([2, 4, 6, 10, 13].includes(month)) return 29;
  if (month === 8) return yearLen % 10 === 5 ? 30 : 29;
  if (month === 9) return yearLen % 10 === 3 ? 29 : 30;
  if (month === 12) return isLeap(year) ? 30 : 29;
  return 30;
}

// Months in year-order starting from Tishri (7)
function yearMonths(year: number): number[] {
  return isLeap(year)
    ? [7, 8, 9, 10, 11, 12, 13, 1, 2, 3, 4, 5, 6]
    : [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];
}

function hebrewToJDN(year: number, month: number, day: number): number {
  let jdn = newYear(year) + day - 1;
  for (const m of yearMonths(year)) {
    if (m === month) break;
    jdn += monthLen(m, year);
  }
  return jdn;
}

function gregorianToJDN(y: number, mo: number, d: number): number {
  const a = Math.floor((14 - mo) / 12);
  const yr = y + 4800 - a;
  const mn = mo + 12 * a - 3;
  return (
    d +
    Math.floor((153 * mn + 2) / 5) +
    365 * yr +
    Math.floor(yr / 4) -
    Math.floor(yr / 100) +
    Math.floor(yr / 400) -
    32045
  );
}

function jdnToGregorian(jdn: number): Date {
  const a = jdn + 32044;
  const b = Math.floor((4 * a + 3) / 146097);
  const c = a - Math.floor((146097 * b) / 4);
  const d2 = Math.floor((4 * c + 3) / 1461);
  const e = c - Math.floor((1461 * d2) / 4);
  const m = Math.floor((5 * e + 2) / 153);
  const day = e - Math.floor((153 * m + 2) / 5) + 1;
  const month = m + 3 - 12 * Math.floor(m / 10);
  const year = 100 * b + d2 - 4800 + Math.floor(m / 10);
  return new Date(year, month - 1, day);
}

function jdnToHebrew(jdn: number): { year: number; month: number; day: number } {
  let year = Math.floor((98496 * (jdn - EPOCH)) / 35975351) + 1;
  while (newYear(year) > jdn) year--;
  while (newYear(year + 1) <= jdn) year++;

  let start = newYear(year);
  let month = 7;
  for (const m of yearMonths(year)) {
    const len = monthLen(m, year);
    if (jdn < start + len) {
      month = m;
      break;
    }
    start += len;
  }

  return { year, month, day: jdn - start + 1 };
}

// ─── Gematria (Hebrew numerals) ───────────────────────────────────────────────

const GEM: [number, string][] = [
  [400, 'ת'],
  [300, 'ש'],
  [200, 'ר'],
  [100, 'ק'],
  [90, 'צ'],
  [80, 'פ'],
  [70, 'ע'],
  [60, 'ס'],
  [50, 'נ'],
  [40, 'מ'],
  [30, 'ל'],
  [20, 'כ'],
  [10, 'י'],
  [9, 'ט'],
  [8, 'ח'],
  [7, 'ז'],
  [6, 'ו'],
  [5, 'ה'],
  [4, 'ד'],
  [3, 'ג'],
  [2, 'ב'],
  [1, 'א'],
];

function gematria(n: number): string {
  if (n === 15) return 'ט״ו';
  if (n === 16) return 'ט״ז';
  let r = '';
  let rem = n;
  for (const [v, l] of GEM) {
    while (rem >= v) {
      r += l;
      rem -= v;
    }
  }
  return r.length === 1 ? r + '׳' : r.slice(0, -1) + '״' + r.slice(-1);
}

const MONTH_NAMES: Record<number, string> = {
  1: 'ניסן',
  2: 'אייר',
  3: 'סיון',
  4: 'תמוז',
  5: 'אב',
  6: 'אלול',
  7: 'תשרי',
  8: 'חשון',
  9: 'כסלו',
  10: 'טבת',
  11: 'שבט',
  12: 'אדר',
  13: 'אדר ב׳',
};

const MONTH_NAMES_LEAP: Record<number, string> = { ...MONTH_NAMES, 12: 'אדר א׳' };

// ─── Public API ───────────────────────────────────────────────────────────────

/** Hebrew date of a Gregorian date, split into display parts (day/month in Hebrew, year in gematria). */
export function hebrewDateParts(date: Date): { day: string; month: string; year: string } {
  const jdn = gregorianToJDN(date.getFullYear(), date.getMonth() + 1, date.getDate());
  const { year, month, day } = jdnToHebrew(jdn);
  const names = isLeap(year) ? MONTH_NAMES_LEAP : MONTH_NAMES;
  return { day: gematria(day), month: names[month], year: gematria(year % 1000) };
}

export function hebrewDateString(date: Date): string {
  const { day, month, year } = hebrewDateParts(date);
  return `${day} ${month} ${year}`;
}

/** "Month Year" Hebrew label, e.g. "תשרי תשפ״ז". */
export function hebrewMonthYearString(date: Date): string {
  const { month, year } = hebrewDateParts(date);
  return `${month} ${year}`;
}

/**
 * Returns Jewish holidays for the given Gregorian month as
 * a Map<"year-month-day", string[]> (same key format as CalendarPage).
 */
export function getJewishHolidays(gregYear: number, gregMonth: number): Map<string, string[]> {
  const result = new Map<string, string[]>();

  // Determine which Hebrew year(s) overlap with this Gregorian month
  const hebrewYears = new Set<number>();
  for (const d of [1, 15]) {
    const { year } = jdnToHebrew(gregorianToJDN(gregYear, gregMonth + 1, d));
    hebrewYears.add(year);
  }

  const addHoliday = (hy: number, month: number, day: number, name: string) => {
    if (day < 1 || day > monthLen(month, hy)) return;
    try {
      const gDate = jdnToGregorian(hebrewToJDN(hy, month, day));
      if (gDate.getFullYear() === gregYear && gDate.getMonth() === gregMonth) {
        const key = `${gregYear}-${gregMonth}-${gDate.getDate()}`;
        const list = result.get(key) ?? [];
        list.push(name);
        result.set(key, list);
      }
    } catch {
      // skip
    }
  };

  hebrewYears.forEach((hy) => {
    const purimMonth = isLeap(hy) ? 13 : 12;

    // Fixed holidays [month, day, name]
    const fixed: [number, number, string][] = [
      [7, 1, 'ראש השנה'],
      [7, 2, 'ראש השנה'],
      [7, 3, 'צום גדליה'],
      [7, 10, 'יום כיפור'],
      [7, 15, 'סוכות'],
      [7, 22, 'שמיני עצרת'],
      [7, 23, 'שמחת תורה'],
      [10, 10, 'צום עשרה בטבת'],
      [11, 15, 'ט״ו בשבט'],
      [purimMonth, 14, 'פורים'],
      [1, 15, 'פסח'],
      [1, 16, 'פסח'],
      [1, 21, 'שביעי של פסח'],
      [1, 22, 'אחרון של פסח'],
      [1, 27, 'יום השואה'],
      [2, 4, 'יום הזיכרון'],
      [2, 5, 'יום העצמאות'],
      [2, 18, 'ל״ג בעומר'],
      [2, 28, 'יום ירושלים'],
      [3, 6, 'שבועות'],
      [3, 7, 'שבועות'],
      [4, 17, 'שבעה עשר בתמוז'],
      [5, 9, 'תשעה באב'],
    ];

    fixed.forEach(([m, d, name]) => addHoliday(hy, m, d, name));

    // Chanukah: 8 days starting 25 Kislev
    for (let i = 0; i < 8; i++) {
      let m = 9;
      let d = 25 + i;
      if (d > monthLen(9, hy)) {
        d -= monthLen(9, hy);
        m = 10;
      }
      addHoliday(hy, m, d, 'חנוכה');
    }

    // Rosh Chodesh (1st of each month; skip Tishri = Rosh Hashanah already listed)
    for (const m of [8, 9, 10, 11, 12, 13, 1, 2, 3, 4, 5, 6]) {
      if (m === 13 && !isLeap(hy)) continue;
      addHoliday(hy, m, 1, 'ראש חודש');
      // 30th of prior month is also Rosh Chodesh
      const prevM = m === 1 ? (isLeap(hy) ? 13 : 12) : m === 7 ? 6 : m - 1;
      if (monthLen(prevM, hy) === 30) {
        addHoliday(hy, prevM, 30, 'ראש חודש');
      }
    }
  });

  return result;
}
